import { Image, Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import type { ImageSourcePropType } from 'react-native';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Asset } from 'expo-asset';

import { Container } from '@/components/ui/Container';
import { Pill } from '@/components/ui/Pill';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { SeoHead } from '@/components/SeoHead';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

/* Web transition helpers — passed through by react-native-web to inline CSS. */
const webTransition = (props = 'transform, background-color, box-shadow, border-color, opacity', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

const HERO_IMAGE = require('../../../assets/marketing/hero-main.webp');
const heroImageUri = Platform.OS === 'web' ? Asset.fromModule(HERO_IMAGE).uri : undefined;

/* ──────────────────────────────────────────────────────────────────────── */
/* HOME — pixel-faithful port of Zoukessence Home.html                       */
/* ──────────────────────────────────────────────────────────────────────── */

export default function HomeScreen() {
  const { t } = useTranslation();
  return (
    <View>
      <SeoHead
        title={t('home.seo.title')}
        description={t('home.seo.description')}
      />
      <Hero />
      <Marquee />
      <DancesSection />
      <ModulSection />
      <ScheduleSection />
      <ChallengeSection />
      <TeamSection />
      <CommunitySection />
      <TestimonialsSection />
      <FaqSection />
      <NewsletterSection />
    </View>
  );
}

/* ─── 00 Hero ───────────────────────────────────────────────────────────── */

function Hero() {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <View style={{ paddingTop: 48, paddingBottom: 56 }}>
      <Container>
        <View style={hero.eyebrowRow}>
          <View style={[hero.dot, { backgroundColor: theme.colors.accentAlt }]} />
          <Text variant="meta" tone="fgMute">
            {t('home.hero.eyebrow')}
          </Text>
        </View>

        <View style={hero.grid}>
          <View style={hero.copy}>
            <Text style={[hero.title, { color: theme.colors.fg, fontFamily: theme.fontFamily.display }]}>
              {t('home.hero.titlePart1')}
              <Text style={[hero.title, { color: theme.colors.accent, fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic' }]}>{t('home.hero.titleItalic1')}</Text>
              {t('home.hero.titleConnector')}
              <Text style={[hero.title, { color: theme.colors.accentAlt, fontFamily: theme.fontFamily.displayItalic, fontStyle: 'italic' }]}>{t('home.hero.titleItalic2')}</Text>
              {t('home.hero.titlePart2')}
            </Text>

            <Text variant="h2" style={[hero.subtitle, { color: theme.colors.fgSoft }]}>
              {t('home.hero.subtitlePlain')}<Text variant="h2" italic style={{ color: theme.colors.accent }}>{t('home.hero.subtitleItalic')}</Text>
            </Text>

            <Text variant="bodyLarge" tone="fgSoft" style={hero.lede}>
              {t('home.hero.lede')}
            </Text>

            <View style={hero.ctaRow}>
              <Button variant="primary" size="lg" trailingArrow href="/kurse">
                {t('home.hero.primaryCta')}
              </Button>
              <Button variant="ghost" size="lg" href="/kurse">
                {t('home.hero.ghostCta')}
              </Button>
            </View>
          </View>

          <View style={[hero.sideCard, { backgroundColor: palette.wine }]}>
            {/* Plain DOM <img> on web: RN(-web) Image only attaches the source
                after hydration, but the hero is the LCP element and must be in
                the static HTML so it paints before any JS runs. */}
            {Platform.OS === 'web' ? (
              <img
                src={heroImageUri}
                alt={t('home.hero.sideImageAlt')}
                fetchPriority="high"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Image
                source={HERO_IMAGE}
                style={stripesStyle.full}
                resizeMode="cover"
                accessibilityLabel={t('home.hero.sideImageAlt')}
              />
            )}
            <View style={hero.sideOverlay} />
            <View style={hero.sideContent}>
              <View style={[hero.sideBadge, { backgroundColor: palette.paper }]}>
                <Text variant="meta" style={{ color: palette.ink, fontFamily: 'JetBrainsMono_500Medium' }}>
                  {t('home.hero.sideBadge')}
                </Text>
              </View>
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 20 }}>
                <Text
                  italic
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: 28,
                    lineHeight: 34,
                    color: palette.paper,
                    textAlign: 'center',
                    maxWidth: 300,
                  }}
                >
                  {t('home.hero.sideQuote')}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[hero.meta, { borderTopColor: theme.colors.rule }]}>
          <MetaItem
            label={t('home.hero.metaNext')}
            value={
              <>
                {t('home.hero.metaNextDayPrefix')}
                <Text
                  italic
                  style={{
                    color: theme.colors.accent,
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: 22,
                  }}
                >
                  {t('home.hero.metaNextValue')}
                </Text>
              </>
            }
          />
          <MetaItem label={t('home.hero.metaStudio')} value={t('home.hero.metaStudioValue')} />
          <MetaItem label={t('home.hero.metaLanguage')} value={t('home.hero.metaLanguageValue')} />
          <MetaItem label={t('home.hero.metaCommunity')} value={t('home.hero.metaCommunityValue')} />
        </View>
      </Container>
    </View>
  );
}

function MetaItem({ label, value }: { label: string; value: React.ReactNode }) {
  const theme = useTheme();
  return (
    <View style={hero.metaItem}>
      <Text variant="eyebrow" tone="fgMute">{label}</Text>
      <Text variant="h4" style={{ color: theme.colors.fg, marginTop: 6 }}>{value as any}</Text>
    </View>
  );
}

/* ─── Marquee ───────────────────────────────────────────────────────────── */

function Marquee() {
  const theme = useTheme();
  const { t } = useTranslation();
  const items = t('home.marquee', { returnObjects: true }) as string[];
  const doubled = [...items, ...items, ...items, ...items];

  return (
    <View
      style={[
        marquee.outer,
        { backgroundColor: theme.colors.bg, borderTopColor: theme.colors.rule, borderBottomColor: theme.colors.rule },
      ]}
    >
      <View
        style={[
          marquee.track,
          Platform.OS === 'web' ? ({ animation: 'zk-marquee 40s linear infinite' } as any) : null,
        ]}
      >
        {doubled.map((it, i) => {
          const italic = it === 'Lambada';
          return (
            <View key={`${it}-${i}`} style={marquee.item}>
              <Text
                style={{
                  fontFamily: italic ? theme.fontFamily.displayItalic : theme.fontFamily.display,
                  fontStyle: italic ? 'italic' : 'normal',
                  fontSize: 56,
                  lineHeight: 64,
                  color: italic ? theme.colors.accent : theme.colors.fg,
                }}
              >
                {it}
              </Text>
              <View style={[marquee.dot, { backgroundColor: theme.colors.accentAlt }]} />
            </View>
          );
        })}
      </View>
    </View>
  );
}

/* ─── 01 Drei Tänze ─────────────────────────────────────────────────────── */

type DanceItem = {
  idx: string;
  code: string;
  tag: string;
  title: string;
  body: string;
  cta: string;
  color: string;
  alt: string;
  image?: ImageSourcePropType;
  imageMode?: 'cover' | 'contain';
};

const danceVisuals: Array<{ color: string; alt: string; image?: ImageSourcePropType; imageMode?: 'cover' | 'contain' }> = [
  {
    color: palette.wine,
    alt: palette.wineDeep,
    image: require('../../../assets/marketing/dance-zouk.webp'),
  },
  {
    color: palette.ember,
    alt: '#c93b6a',
    image: require('../../../assets/marketing/dance-lambada.webp'),
  },
  {
    color: palette.verdeSlate,
    alt: '#1c2a4a',
    image: require('../../../assets/marketing/dance-samba.webp'),
  },
];

function DancesSection() {
  const { t } = useTranslation();
  const items = t('home.dances.items', { returnObjects: true }) as Array<{
    idx: string;
    code: string;
    tag: string;
    title: string;
    body: string;
    cta: string;
  }>;
  const dances: DanceItem[] = items.map((it, i) => ({
    ...it,
    ...danceVisuals[i],
  }));
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <SectionHead idx={t('home.dances.eyebrow')} title={t('home.dances.title')} italic={t('home.dances.titleItalic')} />
        <View style={dance.row}>
          {dances.map((d) => (
            <DanceCard key={d.code} d={d} />
          ))}
        </View>
      </Container>
    </View>
  );
}

function DanceCard({ d }: { d: DanceItem }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      accessibilityRole="link"
      style={[
        dance.card,
        {
          borderColor: theme.colors.rule,
          borderRadius: theme.radii.md,
          backgroundColor: theme.colors.bgSoft,
          transform: hovered ? [{ translateY: -4 }] : undefined,
          shadowColor: theme.colors.fg,
          shadowOpacity: hovered ? 0.18 : 0,
          shadowRadius: hovered ? 28 : 0,
          shadowOffset: { width: 0, height: hovered ? 18 : 0 },
        },
        webTransition(),
      ]}
    >
      <View style={[dance.media, { backgroundColor: d.color }]}>
        {d.image ? (
          <Image
            source={d.image}
            style={stripesStyle.full}
            resizeMode={d.imageMode ?? 'cover'}
            accessibilityLabel={d.title}
          />
        ) : (
          <DiagonalStripes color={d.color} alt={d.alt} />
        )}
        <View style={dance.mediaOverlay} />
        <View style={[dance.mediaContent, { justifyContent: 'flex-end' }]}>
          <Text
            numberOfLines={1}
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(48, 8, 72) as any,
              lineHeight: fluidLineHeight(1.05, 76) as any,
              color: palette.paper,
              letterSpacing: -1,
            }}
          >
            {d.code}
          </Text>
        </View>
      </View>
      <View style={dance.body}>
        <View style={dance.tagRow}>
          <Pill variant="ghost">{d.tag}</Pill>
        </View>
        <Text
          style={{
            fontFamily: theme.fontFamily.display,
            fontSize: 36,
            lineHeight: 38,
            color: theme.colors.fg,
            marginTop: 12,
          }}
        >
          {d.title}
        </Text>
        <Text variant="body" tone="fgSoft" style={{ marginTop: 12 }}>
          {d.body}
        </Text>
        <View style={[dance.ctaRow, { borderTopColor: theme.colors.rule }]}>
          <Text
            style={[
              {
                color: theme.colors.accent,
                fontFamily: theme.fontFamily.bodyMedium,
                fontWeight: '500',
              },
              webTransition('transform', '220ms'),
              hovered ? { transform: [{ translateX: 3 }] } : null,
            ]}
          >
            {d.cta} →
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

/* ─── 02 Modul A (full-bleed wine) ──────────────────────────────────────── */

function ModulSection() {
  const { t } = useTranslation();
  const modulCards = t('home.modul.cards', { returnObjects: true }) as Array<{
    label: string;
    title: string;
    meta: string;
    body: string;
    dates: Array<{ date: string; season: string }>;
    price: string;
  }>;
  return (
    <View style={[modul.outer, { backgroundColor: palette.wine }]}>
      <Container>
        <View style={modul.head}>
          <Text variant="eyebrow" style={{ color: 'rgba(245,240,255,0.78)' }}>
            {t('home.modul.eyebrow')}
          </Text>
          <Text
            style={{
              fontFamily: 'InstrumentSerif_400Regular',
              fontSize: fluidFontSize(40, 7, 64) as any,
              lineHeight: fluidLineHeight(1.05, 64) as any,
              color: palette.paper,
              marginTop: 16,
              letterSpacing: -1,
            }}
          >
            {t('home.modul.titlePart1')}
            <Text style={{ fontFamily: 'InstrumentSerif_400Regular_Italic', fontStyle: 'italic', color: palette.emberSoft }}>
              {t('home.modul.titleItalic')}
            </Text>
          </Text>
          <Text style={{ color: 'rgba(245,240,255,0.78)', fontSize: 17, lineHeight: 26, marginTop: 16, maxWidth: 620 }}>
            {t('home.modul.lede')}
          </Text>
        </View>

        <View style={modul.row}>
          {modulCards.map((c) => (
            <View
              key={c.title}
              style={[modul.card, { backgroundColor: palette.wineDeep, borderColor: 'rgba(245,240,255,0.18)' }]}
            >
              <Text variant="meta" style={{ color: palette.emberSoft }}>
                {c.label}
              </Text>
              <Text
                style={{
                  fontFamily: 'InstrumentSerif_400Regular',
                  fontSize: 40,
                  lineHeight: 44,
                  color: palette.paper,
                  marginTop: 12,
                }}
              >
                {c.title}
              </Text>
              <Text variant="meta" style={{ color: 'rgba(245,240,255,0.85)', marginTop: 8 }}>
                {c.meta}
              </Text>
              <Text style={{ color: 'rgba(245,240,255,0.78)', fontSize: 15, lineHeight: 24, marginTop: 16 }}>
                {c.body}
              </Text>

              <View style={modul.dates}>
                {c.dates.map((d, i) => (
                  <View
                    key={d.date}
                    style={[
                      modul.dateRow,
                      { borderColor: 'rgba(245,240,255,0.16)' },
                      i === 0 && { borderTopWidth: 1 },
                    ]}
                  >
                    <Text
                      italic
                      style={{
                        fontFamily: 'InstrumentSerif_400Regular_Italic',
                        fontStyle: 'italic',
                        fontSize: 20,
                        color: palette.paper,
                      }}
                    >
                      {d.date}
                    </Text>
                    <Text variant="meta" style={{ color: 'rgba(245,240,255,0.7)' }}>
                      {d.season}
                    </Text>
                  </View>
                ))}
              </View>

              <View style={modul.foot}>
                <View>
                  <Text
                    italic
                    style={{
                      fontFamily: 'InstrumentSerif_400Regular_Italic',
                      fontStyle: 'italic',
                      fontSize: 36,
                      color: palette.paper,
                    }}
                  >
                    {c.price}
                  </Text>
                </View>
                <Button variant="ember" size="md" trailingArrow href="/kurse">
                  {t('home.modul.cta')}
                </Button>
              </View>
            </View>
          ))}
        </View>
      </Container>
    </View>
  );
}

/* ─── 03 Schedule (row list) ────────────────────────────────────────────── */

type Row = {
  day: string;
  time: string;
  title: string;
  pills: { label: string; variant: 'wine' | 'ember' | 'verde' | 'solid' | 'ghost' }[];
  desc: string;
  price: string;
  cta: string;
};

function ScheduleSection() {
  'use no memo';
  const theme = useTheme();
  const { t } = useTranslation();
  const schedule = t('home.schedule.rows', { returnObjects: true }) as Row[];
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <SectionHead idx={t('home.schedule.eyebrow')} title={t('home.schedule.title')} italic={t('home.schedule.titleItalic')} />
        <Text variant="body" tone="fgSoft" style={{ marginTop: -32, marginBottom: 32, maxWidth: 640 }}>
          {t('home.schedule.lede')}
        </Text>

        <View style={{ borderTopWidth: 1, borderTopColor: theme.colors.rule }}>
          {schedule.map((r) => (
            <ScheduleRow key={r.day + r.time + r.title} r={r} />
          ))}
        </View>
      </Container>
    </View>
  );
}

function ScheduleRow({ r }: { r: Row }) {
  'use no memo';
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        schedRow.row,
        {
          borderBottomColor: theme.colors.rule,
          backgroundColor: hovered ? theme.colors.bgSoft : 'transparent',
          marginHorizontal: hovered ? -12 : 0,
          paddingHorizontal: hovered ? 12 : 0,
          borderRadius: hovered ? 8 : 0,
        },
        webTransition('background-color, padding-left, padding-right, margin'),
      ]}
    >
              <View style={schedRow.when}>
                <Text variant="meta" tone="fgMute" style={{ marginBottom: 4 }}>
                  {r.day}
                </Text>
                <Text
                  italic
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: 28,
                    color: theme.colors.accent,
                  }}
                >
                  {r.time}
                </Text>
              </View>
              <View style={schedRow.what}>
                <Text
                  style={{
                    fontFamily: theme.fontFamily.display,
                    fontSize: 22,
                    lineHeight: 26,
                    color: theme.colors.fg,
                  }}
                >
                  {r.title}
                </Text>
                <View style={schedRow.pills}>
                  {r.pills.map((p) => (
                    <Pill key={p.label} variant={p.variant}>
                      {p.label}
                    </Pill>
                  ))}
                </View>
              </View>
              <View style={schedRow.desc}>
                <Text variant="meta" tone="fgSoft">
                  {r.desc}
                </Text>
              </View>
              <View style={schedRow.price}>
                <Text
                  style={{
                    fontFamily: theme.fontFamily.display,
                    fontSize: 22,
                    color: theme.colors.fg,
                  }}
                >
                  {r.price}
                </Text>
              </View>
      <View style={schedRow.action}>
        <Button variant="primary" size="sm" trailingArrow href="/kurse">
          {r.cta}
        </Button>
      </View>
    </Pressable>
  );
}

/* ─── 04 Zouk Challenge (dark + arc) ────────────────────────────────────── */

type ChallengeCardData = {
  date: string;
  code: string;
  title: string;
  desc: string;
};

function ChallengeSection() {
  const { t } = useTranslation();
  const challengeCards = t('home.challenge.cards', { returnObjects: true }) as ChallengeCardData[];
  return (
    <View
      style={[
        challenge.outer,
        {
          backgroundColor: palette.night,
        },
        Platform.OS === 'web'
          ? ({
              boxShadow: `0 0 80px -20px ${palette.wine}55, 0 24px 60px -24px #15082E80`,
            } as any)
          : null,
      ]}
    >
      {/* Top-right arc — circle with internal ember radial glow */}
      <View
        style={[
          challenge.arc,
          Platform.OS === 'web'
            ? ({
                backgroundImage: `radial-gradient(circle at 30% 30%, ${palette.ember}55 0%, transparent 60%)`,
              } as any)
            : null,
        ]}
      />
      {/* Bottom-left ember halo */}
      <View
        style={[
          challenge.haloBL,
          Platform.OS === 'web'
            ? ({
                backgroundImage: `radial-gradient(circle, ${palette.ember}30 0%, transparent 65%)`,
              } as any)
            : null,
        ]}
      />

      <Container>
        <SectionHead
          idx={t('home.challenge.eyebrow')}
          title={t('home.challenge.title')}
          italic={t('home.challenge.titleItalic')}
          sub={t('home.challenge.lede')}
          invert
        />

        <View style={challenge.banner}>
          <Image
            source={require('../../../assets/marketing/zouk-challenge-banner.jpg')}
            style={challenge.bannerImage}
            resizeMode="cover"
            accessibilityLabel={t('home.challenge.bannerAlt')}
          />
        </View>

        <View style={[challenge.threeWrap, { borderColor: 'rgba(245,240,255,0.18)', backgroundColor: 'rgba(245,240,255,0.18)' }]}>
          {challengeCards.map((c, i) => (
            <ChallengeCard key={c.code} c={c} last={i === challengeCards.length - 1} />
          ))}
        </View>

        <View style={challenge.foot}>
          <Text style={{ color: 'rgba(245,240,255,0.65)', fontSize: 14, lineHeight: 22, flex: 1, minWidth: 280 }}>
            {t('home.challenge.footTime')}  ·  {t('home.challenge.footWith')}
            <Text style={{ color: palette.paper, fontWeight: '500' }}>{t('home.challenge.footTeachers')}</Text>  ·  {' '}
            <Text style={{ color: palette.paper, fontWeight: '500' }}>{t('home.challenge.footPrice')}</Text>{t('home.challenge.footPriceSuffix')}
          </Text>
          <Button variant="ember" size="lg" trailingArrow href="/kurse">
            {t('home.challenge.cta')}
          </Button>
        </View>
      </Container>
    </View>
  );
}

function ChallengeCard({ c, last }: { c: ChallengeCardData; last: boolean }) {
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        challenge.card,
        {
          backgroundColor: hovered ? '#1f1116' : palette.night,
          marginRight: last ? 0 : 1, // create 1px dividers via parent bg showing through
        },
        webTransition('background-color', '220ms'),
      ]}
    >
      <Text
        style={{
          fontFamily: 'JetBrainsMono_500Medium',
          fontSize: 11,
          letterSpacing: 1.8,
          textTransform: 'uppercase',
          color: 'rgba(245,240,255,0.5)',
        }}
      >
        {c.date}
      </Text>
      <Text
        style={{
          fontFamily: 'InstrumentSerif_400Regular',
          fontSize: 38,
          lineHeight: 40,
          color: palette.emberSoft,
          marginTop: 14,
        }}
      >
        {c.code}
      </Text>
      <Text
        italic
        style={{
          fontFamily: 'InstrumentSerif_400Regular_Italic',
          fontStyle: 'italic',
          fontSize: 28,
          lineHeight: 32,
          color: palette.paper,
          marginTop: 2,
        }}
      >
        {c.title}
      </Text>
      <Text style={{ color: 'rgba(245,240,255,0.66)', fontSize: 14, lineHeight: 22, marginTop: 12 }}>
        {c.desc}
      </Text>
    </Pressable>
  );
}

/* ─── 05 Team (striped portraits) ───────────────────────────────────────── */

type TeamItem = {
  name: string;
  role: string;
  specs: string;
  bio: string;
  color: string;
  alt: string;
  photo?: ImageSourcePropType;
};

const teamVisuals: Array<{ color: string; alt: string; photo?: ImageSourcePropType }> = [
  {
    color: palette.wine,
    alt: palette.wineDeep,
    photo: require('../../../assets/team/manu.jpg'),
  },
  {
    color: palette.ember,
    alt: '#c93b6a',
    photo: require('../../../assets/team/corinne.jpg'),
  },
  {
    color: palette.verdeSlate,
    alt: '#1c2a4a',
    photo: require('../../../assets/team/michelle.jpg'),
  },
  {
    color: palette.gold,
    alt: '#c8a04a',
    photo: require('../../../assets/team/cyrill.jpg'),
  },
];

function TeamSection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const teamItems = t('home.team.items', { returnObjects: true }) as Array<{
    name: string;
    role: string;
    specs: string;
    bio: string;
  }>;
  const team: TeamItem[] = teamItems.map((it, i) => ({
    ...it,
    ...teamVisuals[i],
  }));
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <SectionHead idx={t('home.team.eyebrow')} title={t('home.team.title')} italic={t('home.team.titleItalic')} />
        <Text variant="body" tone="fgSoft" style={{ marginTop: -32, marginBottom: 32, maxWidth: 640 }}>
          {t('home.team.lede')}
        </Text>

        <View style={teamStyle.grid}>
          {team.map((tm) => (
            <View key={tm.name} style={teamStyle.tile}>
              <View style={[teamStyle.portrait, { backgroundColor: tm.color }]}>
                {tm.photo ? (
                  <Image
                    source={tm.photo}
                    style={stripesStyle.full}
                    resizeMode="cover"
                    accessibilityLabel={tm.name}
                  />
                ) : (
                  <DiagonalStripes color={tm.color} alt={tm.alt} />
                )}
                <View style={teamStyle.portraitOverlay} />
                <View style={teamStyle.portraitTop}>
                  <Text variant="meta" style={{ color: 'rgba(245,240,255,0.85)' }}>
                    {tm.role}
                  </Text>
                </View>
              </View>
              <Text variant="h4" style={{ color: theme.colors.fg, marginTop: 12 }}>
                {tm.name}
              </Text>
              <Text variant="meta" tone="fgSoft" style={{ marginTop: 6 }}>
                {tm.specs}
              </Text>
              <Text variant="body" tone="fgSoft" style={{ marginTop: 12 }}>
                {tm.bio}
              </Text>
            </View>
          ))}
        </View>
      </Container>
    </View>
  );
}

/* ─── 06 Community + Stats + Events ─────────────────────────────────────── */

type StatItem = { n: string; suffix: string; label: string; small: string };
type EventItem = { num: string; mon: string; title: string; sub: string };

function CommunitySection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const stats = t('home.community.stats', { returnObjects: true }) as StatItem[];
  const events = t('home.community.events', { returnObjects: true }) as EventItem[];
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <SectionHead idx={t('home.community.eyebrow')} title={t('home.community.title')} italic={t('home.community.titleItalic')} />
        <Text variant="body" tone="fgSoft" style={{ marginTop: -32, marginBottom: 32, maxWidth: 640 }}>
          {t('home.community.lede')}
        </Text>

        <View style={communityStyle.split}>
          <View style={communityStyle.left}>
            <Text
              style={{
                fontFamily: theme.fontFamily.display,
                fontSize: 36,
                lineHeight: 42,
                color: theme.colors.fg,
                maxWidth: 360,
              }}
            >
              {t('home.community.splitTextPart1')}
              <Text
                italic
                style={{
                  fontFamily: theme.fontFamily.displayItalic,
                  fontStyle: 'italic',
                  color: theme.colors.accentAlt,
                  fontSize: 36,
                  lineHeight: 42,
                }}
              >
                {t('home.community.splitTextItalic')}
              </Text>
              {t('home.community.splitTextPart2')}
            </Text>

            <View style={communityStyle.statGrid}>
              {stats.map((s) => (
                <View key={s.label} style={[communityStyle.stat, { borderTopColor: theme.colors.rule }]}>
                  <Text
                    style={{
                      fontFamily: theme.fontFamily.display,
                      fontSize: 64,
                      lineHeight: 64,
                      color: theme.colors.accent,
                    }}
                  >
                    {s.n}
                    <Text
                      italic
                      style={{
                        fontFamily: theme.fontFamily.displayItalic,
                        fontStyle: 'italic',
                        color: theme.colors.accentAlt,
                        fontSize: 64,
                      }}
                    >
                      {s.suffix}
                    </Text>
                  </Text>
                  <Text variant="meta" tone="fgMute" style={{ marginTop: 6 }}>
                    {s.label}
                  </Text>
                  <Text variant="small" tone="fgSoft" style={{ marginTop: 4 }}>
                    {s.small}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          <View style={communityStyle.right}>
            <Text variant="eyebrow" tone="fgMute" style={{ marginBottom: 14 }}>
              {t('home.community.eventsHeading')}
            </Text>
            {events.map((e) => (
              <EventCard key={e.title} e={e} />
            ))}
          </View>
        </View>
      </Container>
    </View>
  );
}

function EventCard({ e }: { e: EventItem }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        communityStyle.eventCard,
        {
          backgroundColor: hovered ? theme.colors.bg : theme.colors.bgSoft,
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
          borderRadius: theme.radii.md,
        },
        webTransition('background-color, border-color, transform'),
        hovered ? { transform: [{ translateY: -1 }] } : null,
      ]}
    >
                <View style={communityStyle.eventDate}>
                  <Text
                    style={{
                      fontFamily: theme.fontFamily.display,
                      fontSize: 34,
                      color: theme.colors.accent,
                      lineHeight: 34,
                    }}
                  >
                    {e.num}
                  </Text>
                  <Text variant="meta" tone="fgMute" style={{ marginTop: 2 }}>
                    {e.mon}
                  </Text>
                </View>
                <View style={communityStyle.eventInfo}>
                  <Text
                    italic
                    style={{
                      fontFamily: theme.fontFamily.displayItalic,
                      fontStyle: 'italic',
                      fontSize: 22,
                      color: theme.colors.fg,
                    }}
                  >
                    {e.title}
                  </Text>
                  <Text variant="meta" tone="fgSoft" style={{ marginTop: 4 }}>
                    {e.sub}
                  </Text>
                </View>
      <Text
        style={[
          { fontSize: 22, color: hovered ? theme.colors.accent : theme.colors.fgMute },
          webTransition('transform, color', '220ms'),
          hovered ? { transform: [{ translateX: 3 }] } : null,
        ]}
      >
        →
      </Text>
    </Pressable>
  );
}

/* ─── 07 Testimonials ───────────────────────────────────────────────────── */

type TestimonialItem = {
  body: string;
  initial: string;
  name: string;
  meta: string;
};

function TestimonialsSection() {
  const { t } = useTranslation();
  const testimonials = t('home.testimonials.items', { returnObjects: true }) as TestimonialItem[];
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <SectionHead idx={t('home.testimonials.eyebrow')} title={t('home.testimonials.title')} italic={t('home.testimonials.titleItalic')} tail={t('home.testimonials.titleTail')} />
        <View style={testStyle.grid}>
          {testimonials.map((item) => (
            <TestimonialCard key={item.name} t={item} />
          ))}
        </View>
      </Container>
    </View>
  );
}

function TestimonialCard({ t }: { t: TestimonialItem }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        testStyle.card,
        {
          backgroundColor: theme.colors.bgSoft,
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
          borderRadius: theme.radii.md,
        },
        webTransition('border-color, transform, box-shadow'),
        hovered ? { transform: [{ translateY: -2 }] } : null,
      ]}
    >
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 64,
                  lineHeight: 56,
                  color: theme.colors.accentAlt,
                }}
              >
                «
              </Text>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 22,
                  lineHeight: 28,
                  color: theme.colors.fg,
                  marginTop: -12,
                }}
              >
                {t.body}
              </Text>
      <View style={[testStyle.who, { borderTopColor: theme.colors.rule }]}>
        <View style={[testStyle.avatar, { backgroundColor: theme.colors.accent }]}>
          <Text
            italic
            style={{
              fontFamily: theme.fontFamily.displayItalic,
              fontStyle: 'italic',
              fontSize: 18,
              color: palette.paper,
            }}
          >
            {t.initial}
          </Text>
        </View>
        <View>
          <Text variant="body" style={{ fontWeight: '500' }}>
            {t.name}
          </Text>
          <Text variant="meta" tone="fgMute" style={{ marginTop: 2 }}>
            {t.meta}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

/* ─── 08 FAQ (2-col) ────────────────────────────────────────────────────── */

type FaqItem = { q: string; a: string };

function FaqSection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const faqs = t('home.faq.items', { returnObjects: true }) as FaqItem[];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <View style={faqStyle.split}>
          <View style={faqStyle.left}>
            <Text variant="eyebrow" tone="fgMute">{t('home.faq.eyebrow')}</Text>
            <Text
              style={{
                fontFamily: theme.fontFamily.display,
                fontSize: 56,
                lineHeight: 56,
                color: theme.colors.fg,
                marginTop: 16,
              }}
            >
              {t('home.faq.titlePart1')}{'\n'}
              <Text
                italic
                style={{
                  fontFamily: theme.fontFamily.displayItalic,
                  fontStyle: 'italic',
                  color: theme.colors.accent,
                  fontSize: 56,
                }}
              >
                {t('home.faq.titleItalic')}
              </Text>
            </Text>
            <Text variant="body" tone="fgSoft" style={{ marginTop: 16, maxWidth: 360 }}>
              {t('home.faq.ledePart1')}
              <Text style={{ color: theme.colors.accent, fontWeight: '500' }}>{t('home.faq.ledeLink1')}</Text>{t('home.faq.ledeConnector')}
              <Text style={{ color: theme.colors.accent, fontWeight: '500' }}>{t('home.faq.ledeLink2')}</Text>{t('home.faq.ledePart2')}
            </Text>
          </View>

          <View style={faqStyle.right}>
            <View style={{ borderTopWidth: 1, borderTopColor: theme.colors.rule }}>
              {faqs.map((f, i) => {
                const isOpen = open === i;
                return (
                  <View key={f.q} style={[faqStyle.item, { borderBottomColor: theme.colors.rule }]}>
                    <Pressable accessibilityRole="button" onPress={() => setOpen(isOpen ? null : i)} style={faqStyle.row}>
                      <Text
                        style={{
                          fontFamily: theme.fontFamily.display,
                          fontSize: 22,
                          lineHeight: 26,
                          color: theme.colors.fg,
                          flex: 1,
                        }}
                      >
                        {f.q}
                      </Text>
                      <Text
                        style={{
                          fontFamily: theme.fontFamily.mono,
                          fontSize: 16,
                          color: theme.colors.accent,
                          transform: [{ rotate: isOpen ? '45deg' : '0deg' }],
                        }}
                      >
                        +
                      </Text>
                    </Pressable>
                    {isOpen ? (
                      <View style={{ paddingBottom: 22 }}>
                        <Text variant="body" tone="fgSoft" style={{ maxWidth: 560 }}>
                          {f.a}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          </View>
        </View>
      </Container>
    </View>
  );
}

/* ─── 09 Newsletter (card) ──────────────────────────────────────────────── */

function NewsletterSection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  return (
    <View style={{ paddingVertical: 96 }}>
      <Container>
        <View
          style={[
            newsletter.card,
            { backgroundColor: theme.colors.bgSoft, borderColor: theme.colors.rule, borderRadius: theme.radii.lg },
          ]}
        >
          <View style={{ flex: 1, minWidth: 320 }}>
            <Text variant="eyebrow" tone="fgMute">{t('home.newsletter.eyebrow')}</Text>
            <Text
              style={{
                fontFamily: theme.fontFamily.display,
                fontSize: 48,
                lineHeight: 50,
                color: theme.colors.fg,
                marginTop: 16,
              }}
            >
              {t('home.newsletter.titlePart1')}
              <Text
                italic
                style={{
                  fontFamily: theme.fontFamily.displayItalic,
                  fontStyle: 'italic',
                  color: theme.colors.accent,
                  fontSize: 48,
                }}
              >
                {t('home.newsletter.titleItalic')}
              </Text>
            </Text>
            <Text variant="body" tone="fgSoft" style={{ marginTop: 12, maxWidth: 460 }}>
              {t('home.newsletter.lede')}
            </Text>
          </View>
          <View style={{ flex: 1, minWidth: 320 }}>
            <View
              style={[
                newsletter.form,
                { backgroundColor: theme.colors.bg, borderColor: theme.colors.rule, borderRadius: theme.radii.pill },
              ]}
            >
              <TextInput
                value={email}
                onChangeText={setEmail}
                placeholder={t('home.newsletter.placeholder')}
                placeholderTextColor={theme.colors.fgMute}
                style={[
                  newsletter.input,
                  { color: theme.colors.fg, fontFamily: theme.fontFamily.body as string },
                ]}
              />
              <Button variant="primary" size="md" trailingArrow>
                {t('home.newsletter.cta')}
              </Button>
            </View>
            <Text variant="meta" tone="fgMute" style={{ marginTop: 14 }}>
              {t('home.newsletter.contactLine')}
            </Text>
          </View>
        </View>
      </Container>
    </View>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function SectionHead({
  idx,
  title,
  italic,
  tail,
  sub,
  invert,
}: {
  idx: string;
  title: string;
  italic: string;
  tail?: string;
  sub?: string;
  invert?: boolean; // true when inside a midnight section
}) {
  const theme = useTheme();
  const idxColor = invert ? 'rgba(245,240,255,0.6)' : theme.colors.fgMute;
  const titleColor = invert ? palette.paper : theme.colors.fg;
  const italicColor = invert ? palette.emberSoft : theme.colors.accent;
  const subColor = invert ? 'rgba(245,240,255,0.7)' : theme.colors.fgSoft;

  return (
    <View style={sectionHeadStyles.row}>
      <View style={sectionHeadStyles.idxCol}>
        <Text style={[sectionHeadStyles.idx, { color: idxColor }]}>{idx}</Text>
      </View>
      <View style={sectionHeadStyles.body}>
        <Text
          style={{
            fontFamily: theme.fontFamily.display,
            fontSize: 56,
            lineHeight: 56,
            color: titleColor,
            letterSpacing: -1,
          }}
        >
          {title}{' '}
          <Text
            italic
            style={{
              fontFamily: theme.fontFamily.displayItalic,
              fontStyle: 'italic',
              color: italicColor,
              fontSize: 56,
            }}
          >
            {italic}
          </Text>
          {tail ? <Text style={{ color: titleColor }}> {tail}</Text> : null}
        </Text>
        {sub ? (
          <Text
            style={{
              color: subColor,
              fontSize: 17,
              lineHeight: 26,
              marginTop: 14,
              maxWidth: 600,
            }}
          >
            {sub}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const sectionHeadStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    marginBottom: 56,
    alignItems: 'flex-start',
  },
  idxCol: {
    flexBasis: 180,
    flexGrow: 0,
    paddingTop: 8,
  },
  idx: {
    fontFamily: 'JetBrainsMono_500Medium',
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
  },
  body: {
    flex: 1,
    minWidth: 280,
  },
});

function DiagonalStripes({ color, alt }: { color: string; alt: string }) {
  return (
    <View
      style={[
        stripesStyle.full,
        { backgroundColor: color },
        Platform.OS === 'web'
          ? ({
              backgroundImage: `repeating-linear-gradient(135deg, ${color} 0, ${color} 14px, ${alt} 14px, ${alt} 28px)`,
            } as any)
          : null,
      ]}
    />
  );
}

/* ─── Styles ───────────────────────────────────────────────────────────── */

const hero = StyleSheet.create({
  eyebrowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 32,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    alignItems: 'flex-start',
  },
  copy: {
    flex: 1.4,
    minWidth: 320,
    gap: 24,
  },
  title: {
    fontSize: fluidFontSize(48, 11.5, 96) as any,
    lineHeight: fluidLineHeight(0.95, 96) as any,
    letterSpacing: -2,
    fontWeight: '400',
  },
  subtitle: {
    marginTop: -8,
    fontSize: 36,
    lineHeight: 40,
  },
  lede: {
    maxWidth: 540,
    marginTop: 8,
  },
  ctaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 16,
  },
  sideCard: {
    flex: 1,
    minWidth: 320,
    aspectRatio: 3 / 4,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  sideOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(12,8,20,0.45)',
  },
  sideContent: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 24,
    alignItems: 'flex-start',
  },
  sideBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
  },
  meta: {
    marginTop: 56,
    paddingTop: 24,
    borderTopWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
  },
  metaItem: { flexBasis: 220, flexGrow: 1 },
});

const marquee = StyleSheet.create({
  outer: { paddingVertical: 18, borderTopWidth: 1, borderBottomWidth: 1, overflow: 'hidden' },
  track: { flexDirection: 'row', alignItems: 'center', width: '300%' },
  item: { flexDirection: 'row', alignItems: 'center' },
  dot: { width: 10, height: 10, borderRadius: 5, marginHorizontal: 32 },
});

const dance = StyleSheet.create({
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 24 },
  card: { flex: 1, minWidth: 280, borderWidth: 1, overflow: 'hidden' },
  media: { aspectRatio: 5 / 4, position: 'relative', overflow: 'hidden' },
  mediaOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(12,8,20,0.45)',
  },
  mediaContent: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 22,
    justifyContent: 'space-between',
  },
  body: { padding: 24 },
  tagRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  ctaRow: { marginTop: 24, paddingTop: 16, borderTopWidth: 1 },
});

const modul = StyleSheet.create({
  outer: { paddingVertical: 96 },
  head: { marginBottom: 48 },
  row: { flexDirection: 'row', flexWrap: 'wrap', gap: 24 },
  card: { flex: 1, minWidth: 320, padding: 32, borderRadius: 12, borderWidth: 1, gap: 16 },
  dates: { marginTop: 12, gap: 0 },
  dateRow: { paddingVertical: 16, borderBottomWidth: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  foot: { marginTop: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
});

const schedRow = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 22,
    borderBottomWidth: 1,
  },
  when: { flexBasis: 120, flexGrow: 0 },
  what: { flex: 1, minWidth: 240, gap: 8 },
  pills: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  desc: { flexBasis: 220, flexGrow: 0 },
  price: { flexBasis: 100, flexGrow: 0, alignItems: 'flex-end' },
  action: { flexBasis: 120, flexGrow: 0, alignItems: 'flex-end' },
});

const challenge = StyleSheet.create({
  outer: {
    paddingVertical: 96,
    paddingHorizontal: 8,
    marginHorizontal: 24,
    marginVertical: 48,
    borderRadius: 12,
    position: 'relative',
    overflow: 'hidden',
  },
  arc: {
    position: 'absolute',
    right: -200,
    top: -120,
    width: 600,
    height: 600,
    borderRadius: 300,
    borderWidth: 1,
    borderColor: 'rgba(245,240,255,0.10)',
  },
  haloBL: {
    position: 'absolute',
    left: -100,
    bottom: -100,
    width: 500,
    height: 500,
    borderRadius: 250,
  },
  banner: {
    marginTop: 32,
    borderRadius: 12,
    overflow: 'hidden',
    aspectRatio: 1024 / 389,
  },
  bannerImage: {
    width: '100%',
    height: '100%',
  },
  threeWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
    marginTop: 32,
  },
  card: {
    flex: 1,
    minWidth: 260,
    padding: 28,
    marginBottom: 0,
    gap: 4,
  },
  foot: {
    marginTop: 32,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
  },
});

const teamStyle = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 24 },
  tile: { flex: 1, minWidth: 220, gap: 12 },
  portrait: { aspectRatio: 4 / 5, borderRadius: 12, overflow: 'hidden', position: 'relative', padding: 16, justifyContent: 'space-between' },
  portraitOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(12,8,20,0.3)',
  },
  portraitTop: { zIndex: 1 },
  portraitBottom: { zIndex: 1 },
});

const communityStyle = StyleSheet.create({
  split: { flexDirection: 'row', flexWrap: 'wrap', gap: 48 },
  left: { flex: 1.1, minWidth: 320 },
  right: { flex: 1, minWidth: 320 },
  statGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 22, marginTop: 32 },
  stat: { flexBasis: 180, flexGrow: 1, borderTopWidth: 1, paddingTop: 14 },
  eventCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
    padding: 20,
    marginBottom: 12,
    borderWidth: 1,
  },
  eventDate: { width: 70, alignItems: 'center' },
  eventInfo: { flex: 1 },
});

const testStyle = StyleSheet.create({
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 18 },
  card: { flex: 1, minWidth: 280, padding: 30, borderWidth: 1, gap: 18 },
  who: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 'auto', paddingTop: 14, borderTopWidth: 1 },
  avatar: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },
});

const faqStyle = StyleSheet.create({
  split: { flexDirection: 'row', flexWrap: 'wrap', gap: 48 },
  left: { flexBasis: 280, flexGrow: 0 },
  right: { flex: 1, minWidth: 320 },
  item: { borderBottomWidth: 1 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 24, paddingVertical: 22 },
});

const newsletter = StyleSheet.create({
  card: {
    padding: 56,
    borderWidth: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
    alignItems: 'center',
  },
  form: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    paddingLeft: 20,
    gap: 8,
    borderWidth: 1,
  },
  input: {
    flex: 1,
    minWidth: 160,
    fontSize: 15,
    paddingVertical: 8,
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  } as any,
});

const stripesStyle = StyleSheet.create({
  full: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
});
